import { Button } from '@brightdrop/bd-ui';
import { Box, IconButton, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { forwardRef, type ReactNode } from 'react';

import CloseGlyph from '../icons/CloseGlyph';
import useStyles from './InlineMessageCard.styles';

/**
 * Configurable button rendered inside an `InlineMessageCard`. Used
 * for both `primaryAction` (filled brand button, supports a loading
 * state) and `secondaryAction` (outlined secondary button).
 */
export type InlineMessageCardAction = {
  /** Visible button label (already localized). */
  label: string;
  /** Click handler. Ignored while `isLoading` is `true`. */
  onClick: () => void;
  /**
   * Whether to render the action in its loading state. The underlying
   * design-system `Button` swaps its label for the canonical loader
   * icon and disables itself. Only meaningful on `primaryAction`.
   */
  isLoading?: boolean;
  /**
   * Accessible name applied to the primary button while it is in its
   * loading state. Defaults to `label` when omitted.
   */
  loadingAriaLabel?: string;
  /** Optional `data-testid` for downstream test wiring. */
  'data-testid'?: string;
};

/** Subset of {@link InlineMessageCardAction} valid for the secondary slot. */
export type InlineMessageCardSecondaryAction = Omit<
  InlineMessageCardAction,
  'isLoading' | 'loadingAriaLabel'
>;

/**
 * Props common to every variant of the card, regardless of whether a
 * dismiss affordance is rendered.
 */
type InlineMessageCardBaseProps = {
  /** Card title, rendered as an `<h3>`. */
  title: string;
  /**
   * Optional supporting body content. Accepts plain strings or rich
   * nodes; rendered inside a `div` container so callers may safely
   * pass block-level children (lists, paragraphs, custom components)
   * without producing invalid HTML (which a default `<p>` wrapper
   * would, since block elements cannot nest inside a paragraph).
   */
  body?: ReactNode;
  /**
   * Filled primary action (brand-600 button). Optional so the card
   * can also render as a notification with only a dismiss affordance.
   */
  primaryAction?: InlineMessageCardAction;
  /** Outlined secondary action shown to the left of the primary button. */
  secondaryAction?: InlineMessageCardSecondaryAction;
  /** Optional class for surface-level overrides (margin, max-width, …). */
  className?: string;
  /** Optional `data-testid` for the card root. */
  'data-testid'?: string;
  /** Optional tab index for programmatic focus targets. */
  tabIndex?: number;
};

/**
 * Discriminated union for the dismiss affordance: when `onDismiss` is
 * provided the close glyph is rendered, and an accessible name is
 * required so it never ships unlabeled. When omitted, neither prop is
 * accepted, so callers cannot pass a stranded `closeAriaLabel`.
 */
type InlineMessageCardDismissProps =
  | {
      /**
       * Fired when the user clicks the trailing close glyph. The glyph
       * only renders when `onDismiss` is provided. Inert while
       * `primaryAction.isLoading` is `true`.
       */
      onDismiss: () => void;
      /** Localized accessible name for the dismiss glyph (required). */
      closeAriaLabel: string;
    }
  | {
      onDismiss?: undefined;
      closeAriaLabel?: undefined;
    };

export type InlineMessageCardProps = InlineMessageCardBaseProps &
  InlineMessageCardDismissProps;

/**
 * Width applied to both action buttons so the primary keeps the same
 * footprint when its label is swapped for the design-system loader
 * icon. Mirrors the Figma Cancel width (133px) to keep the pair
 * visually balanced regardless of label length or loading state.
 */
const ACTION_BUTTON_WIDTH = '133px';

/**
 * MUI's vanilla `MuiButton-root` ships with `text-transform: uppercase`
 * and the brand override does not always win the cascade once the
 * `bd-ui` styled-components wrapper has tagged the element. Pass the
 * reset explicitly so labels render in sentence case verbatim.
 */
const ACTION_BUTTON_SX = (theme: Theme) => ({
  borderRadius: theme.new?.shape.borderRadius.small ?? '0.5rem',
  fontWeight: 500,
  textTransform: 'none' as const,
});

/**
 * Reusable inline message card. Renders a titled, dismissible card
 * inside the chat surface with an optional primary/secondary action
 * pair. Used today by the in-chat reset confirmation prompt
 * (`ResetConversationPrompt`); kept intentionally generic so future
 * surfaces (sidekick announcements, soft errors, contextual nudges)
 * can reuse the same primitive without re-implementing the chrome.
 *
 * Uses the design-system `Button` from `@brightdrop/bd-ui` so the
 * primary/secondary visual treatment and loading affordance come
 * straight from the shared library — no bespoke spinner logic lives
 * here. The card never owns its own visibility; render it
 * conditionally from the parent.
 */
const InlineMessageCard = forwardRef<HTMLDivElement, InlineMessageCardProps>(
  function InlineMessageCard(
    {
      title,
      body,
      primaryAction,
      secondaryAction,
      onDismiss,
      closeAriaLabel,
      className,
      tabIndex,
      'data-testid': dataTestId,
    },
    ref
  ) {
    const { classes, cx } = useStyles();
    const isLoading = primaryAction?.isLoading === true;

    const handleDismiss = () => {
      if (isLoading) {
        return;
      }
      onDismiss?.();
    };

    const handleSecondaryClick = () => {
      if (isLoading) {
        return;
      }
      secondaryAction?.onClick();
    };

    const handlePrimaryClick = () => {
      if (isLoading) {
        return;
      }
      primaryAction?.onClick();
    };

    return (
      <Box
        ref={ref}
        className={cx(classes.card, className)}
        tabIndex={tabIndex}
        data-testid={dataTestId}
      >
        <Box className={classes.titleRow}>
          <Typography
            variant="small_body_medium"
            component="h3"
            className={classes.title}
          >
            {title}
          </Typography>
          {onDismiss ? (
            <IconButton
              className={classes.closeButton}
              onClick={handleDismiss}
              disabled={isLoading}
              aria-label={closeAriaLabel}
              data-testid="inline-message-card-dismiss"
            >
              <CloseGlyph size={16} />
            </IconButton>
          ) : null}
        </Box>
        {body ? (
          <Typography
            variant="small_body_regular"
            component="div"
            className={classes.body}
            data-testid="inline-message-card-body"
          >
            {body}
          </Typography>
        ) : null}
        {primaryAction || secondaryAction ? (
          <Box className={classes.actionsRow}>
            {secondaryAction ? (
              <Button
                variant="secondary"
                size="medium"
                width={ACTION_BUTTON_WIDTH}
                sx={ACTION_BUTTON_SX}
                onClick={handleSecondaryClick}
                disabled={isLoading}
                data-testid={
                  secondaryAction['data-testid'] ??
                  'inline-message-card-secondary'
                }
              >
                {secondaryAction.label}
              </Button>
            ) : null}
            {primaryAction ? (
              <Button
                variant="primary"
                size="medium"
                width={ACTION_BUTTON_WIDTH}
                sx={ACTION_BUTTON_SX}
                isLoading={isLoading}
                onClick={handlePrimaryClick}
                aria-label={
                  isLoading
                    ? (primaryAction.loadingAriaLabel ?? primaryAction.label)
                    : undefined
                }
                data-testid={
                  primaryAction['data-testid'] ?? 'inline-message-card-primary'
                }
              >
                {primaryAction.label}
              </Button>
            ) : null}
          </Box>
        ) : null}
      </Box>
    );
  }
);

export default InlineMessageCard;
